import { useCallback, useEffect, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionGroup, AccordionSummary,
    Alert, Box, Button, Chip, CircularProgress, FormControl, FormLabel, Input,
    Modal, ModalDialog, Option, Select, Sheet, Stack, Switch, Table, Typography,
} from '@mui/joy';
import { format } from 'date-fns';
import { ru } from 'date-fns/locale';

import { useAdmin } from '../../AdminContext';
import {
    APIAdminDateTreats, APIAdminDateTreatRedeem, APIAdminDateTreatCancel,
    APIAdminDateTreatSettings, APIAdminDateTreatSettingsUpdate, dtError,
} from '../../api_admin_date_treats';

const STATUS = {
    active: { label: 'Активен', color: 'success' },
    redeemed: { label: 'Использован', color: 'primary' },
    expired: { label: 'Истёк', color: 'neutral' },
    cancelled: { label: 'Отменён', color: 'danger' },
};

const STATUS_OPTIONS = [
    { value: '', label: 'Все статусы' },
    { value: 'active', label: 'Активные' },
    { value: 'redeemed', label: 'Использованные' },
    { value: 'expired', label: 'Истёкшие' },
    { value: 'cancelled', label: 'Отменённые' },
];

const money = (v) => Number(v || 0).toLocaleString('ru-RU', { minimumFractionDigits: 0 });

// --- Настройки акции ---
function SettingsPanel({ token, canEdit }) {
    const [items, setItems] = useState([]);
    const [values, setValues] = useState({});
    const [initial, setInitial] = useState({});
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);

    useEffect(() => {
        APIAdminDateTreatSettings(token).then((r) => {
            const its = r?.items || [];
            const v = {};
            its.forEach((it) => { v[it.key] = String(it.value ?? ''); });
            setItems(its); setValues(v); setInitial(v);
        }).catch(() => {});
    }, [token]);

    const changed = Object.fromEntries(
        Object.keys(values).filter((k) => values[k] !== initial[k]).map((k) => [k, values[k]])
    );

    const save = async () => {
        setSaving(true); setMsg(null);
        try {
            await APIAdminDateTreatSettingsUpdate(token, changed);
            setInitial(values);
            setMsg({ color: 'success', text: 'Сохранено' });
        } catch (e) {
            setMsg({ color: 'danger', text: dtError(e) });
        } finally { setSaving(false); }
    };

    return (
        <Sheet variant="soft" sx={{ p: 2, borderRadius: 'md' }}>
            {msg && <Alert color={msg.color} sx={{ mb: 1.5 }}>{msg.text}</Alert>}
            <Stack spacing={1.5}>
                {items.map((it) => (
                    <FormControl key={it.key} orientation="horizontal"
                                 sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
                        <FormLabel sx={{ mb: 0 }}>{it.label}</FormLabel>
                        <Box sx={{ minWidth: 180 }}>
                            {it.type === 'bool' ? (
                                <Switch checked={values[it.key] === 'true'} disabled={!canEdit}
                                        onChange={(e) => setValues({ ...values, [it.key]: e.target.checked ? 'true' : 'false' })} />
                            ) : (
                                <Input type="number" value={values[it.key] ?? ''} disabled={!canEdit}
                                       onChange={(e) => setValues({ ...values, [it.key]: e.target.value })} />
                            )}
                        </Box>
                    </FormControl>
                ))}
                {canEdit && (
                    <Box>
                        <Button size="sm" loading={saving} disabled={!Object.keys(changed).length} onClick={save}>
                            Сохранить настройки
                        </Button>
                    </Box>
                )}
            </Stack>
        </Sheet>
    );
}

// --- Модалка действия (redeem / cancel) ---
function ActionModal({ token, treat, mode, onClose, onDone }) {
    const [note, setNote] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const isRedeem = mode === 'redeem';

    const submit = async () => {
        setSaving(true); setError(null);
        try {
            if (isRedeem) await APIAdminDateTreatRedeem(token, treat.id, note.trim());
            else await APIAdminDateTreatCancel(token, treat.id, note.trim());
            onDone();
            onClose();
        } catch (e) { setError(dtError(e)); } finally { setSaving(false); }
    };

    return (
        <Modal open onClose={onClose}>
            <ModalDialog sx={{ maxWidth: 440, width: '100%' }}>
                <Typography level="h4">
                    {isRedeem ? 'Отметить использованным' : 'Отменить сертификат'} {treat.code}
                </Typography>
                {error && <Alert color="danger" sx={{ my: 1 }}>{error}</Alert>}
                <FormControl sx={{ mt: 1 }}>
                    <FormLabel>{isRedeem ? 'Заведение / комментарий' : 'Причина отмены'}</FormLabel>
                    <Input value={note} onChange={(e) => setNote(e.target.value)} />
                </FormControl>
                <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
                    <Button color={isRedeem ? 'primary' : 'danger'} loading={saving} onClick={submit}>
                        Подтвердить
                    </Button>
                    <Button variant="outlined" color="neutral" onClick={onClose}>Отмена</Button>
                </Stack>
            </ModalDialog>
        </Modal>
    );
}

export default function AdminDateTreatsPage() {
    const { adminToken: token, has } = useAdmin();
    const canEdit = has('date_treats.manage');

    const [items, setItems] = useState([]);
    const [counters, setCounters] = useState({});
    const [redeemedAmount, setRedeemedAmount] = useState(0);
    const [pages, setPages] = useState(1);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [status, setStatus] = useState('');
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [action, setAction] = useState(null); // { treat, mode }

    useEffect(() => {
        const t = setTimeout(() => { setPage(1); setSearch(searchInput); }, 400);
        return () => clearTimeout(t);
    }, [searchInput]);

    const load = useCallback(async () => {
        if (!token) return;
        setLoading(true); setError(null);
        try {
            const res = await APIAdminDateTreats(token, {
                status: status || undefined, search: search || undefined, page, page_size: 30,
            });
            setItems(res?.items || []);
            setCounters(res?.counters || {});
            setRedeemedAmount(res?.redeemed_amount || 0);
            setPages(res?.pages || 1);
            setTotal(res?.total || 0);
        } catch (e) {
            setError(dtError(e, 'Не удалось загрузить сертификаты'));
        } finally { setLoading(false); }
    }, [token, status, search, page]);

    useEffect(() => { load(); }, [load]);

    return (
        <Box>
            <Typography level="h4" sx={{ mb: 0.5 }}>Свидание за наш счёт</Typography>
            <Typography level="body-sm" color="neutral" sx={{ mb: 2 }}>
                Всего: {total} · использовано на {money(redeemedAmount)} ₽
            </Typography>

            <AccordionGroup sx={{ mb: 2 }}>
                <Accordion>
                    <AccordionSummary>Настройки акции</AccordionSummary>
                    <AccordionDetails>
                        <SettingsPanel token={token} canEdit={canEdit} />
                    </AccordionDetails>
                </Accordion>
            </AccordionGroup>

            {error && <Alert color="danger" sx={{ mb: 2 }}>{error}</Alert>}

            <Sheet variant="outlined" sx={{ p: 1.5, borderRadius: 'md', mb: 2 }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems="center">
                    <Input size="sm" placeholder="Код или город" value={searchInput}
                           onChange={(e) => setSearchInput(e.target.value)} sx={{ flex: 1, minWidth: 220 }} />
                    <Select size="sm" value={status} sx={{ minWidth: 190 }}
                            onChange={(_, v) => { setStatus(v ?? ''); setPage(1); }}>
                        {STATUS_OPTIONS.map((o) => <Option key={o.value} value={o.value}>{o.label}</Option>)}
                    </Select>
                    <Stack direction="row" spacing={1}>
                        <Chip size="sm" color="success" variant="soft">активны: {counters.active ?? 0}</Chip>
                        <Chip size="sm" color="primary" variant="soft">использованы: {counters.redeemed ?? 0}</Chip>
                    </Stack>
                </Stack>
            </Sheet>

            {loading ? (
                <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
            ) : (
                <Sheet variant="outlined" sx={{ borderRadius: 'md', overflow: 'auto' }}>
                    <Table hoverRow sx={{ '--TableCell-paddingY': '10px' }}>
                        <thead>
                            <tr>
                                <th style={{ width: 140 }}>Код</th>
                                <th style={{ width: 100 }}>Номинал</th>
                                <th>Пользователь</th>
                                <th style={{ width: 150 }}>Город</th>
                                <th style={{ width: 130 }}>Статус</th>
                                <th style={{ width: 110 }}>Создан</th>
                                <th style={{ width: 190 }}>Действия</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map((t) => {
                                const st = STATUS[t.status] || { label: t.status, color: 'neutral' };
                                return (
                                    <tr key={t.id}>
                                        <td><Typography level="body-sm" sx={{ fontWeight: 600 }}>{t.code}</Typography></td>
                                        <td>{money(t.amount)} ₽</td>
                                        <td>{t.owner}</td>
                                        <td>{t.city || '—'}</td>
                                        <td><Chip size="sm" color={st.color} variant="soft">{st.label}</Chip></td>
                                        <td>{t.date_create ? format(new Date(t.date_create), 'd MMM yy', { locale: ru }) : '—'}</td>
                                        <td>
                                            {canEdit && t.status === 'active' && (
                                                <Stack direction="row" spacing={0.5}>
                                                    <Button size="sm" variant="soft"
                                                            onClick={() => setAction({ treat: t, mode: 'redeem' })}>
                                                        Использован
                                                    </Button>
                                                    <Button size="sm" variant="soft" color="danger"
                                                            onClick={() => setAction({ treat: t, mode: 'cancel' })}>
                                                        Отменить
                                                    </Button>
                                                </Stack>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                            {items.length === 0 && (
                                <tr><td colSpan={7}>
                                    <Typography level="body-sm" color="neutral" sx={{ py: 3, textAlign: 'center' }}>
                                        Сертификатов нет
                                    </Typography>
                                </td></tr>
                            )}
                        </tbody>
                    </Table>
                </Sheet>
            )}

            {pages > 1 && (
                <Stack direction="row" spacing={1} justifyContent="center" sx={{ mt: 2 }}>
                    <Button size="sm" variant="outlined" color="neutral"
                            disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Назад</Button>
                    <Typography level="body-sm" sx={{ alignSelf: 'center' }}>{page} / {pages}</Typography>
                    <Button size="sm" variant="outlined" color="neutral"
                            disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Вперёд</Button>
                </Stack>
            )}

            {action && (
                <ActionModal token={token} treat={action.treat} mode={action.mode}
                             onClose={() => setAction(null)} onDone={load} />
            )}
        </Box>
    );
}